import moment from 'moment-timezone';

const computeProposedWindow = (structure, date, facilityTimezone) => {
	const dayInFacilityTz = moment.tz(moment(date).format('YYYY-MM-DD'), facilityTimezone);
	const [fromHour, fromMinute] = structure.from_time.split(':').map(Number);
	const [toHour, toMinute] = structure.to_time.split(':').map(Number);
	const start = dayInFacilityTz.clone().hour(fromHour).minute(fromMinute).second(0);
	const end = dayInFacilityTz.clone().hour(toHour).minute(toMinute).second(0);
	if (end.isSameOrBefore(start)) {
		end.add(1, 'day');
	}
	return { start: start.valueOf(), end: end.valueOf() };
};

const shiftsOverlap = (existingShift, proposed) => {
	const existingStart = new Date(existingShift.start_time).getTime();
	const existingEnd = new Date(existingShift.end_time).getTime();
	return existingStart < proposed.end && existingEnd > proposed.start;
};

/**
 * Returns the conflict type for a single (structure, date) pair against the
 * caregiver's schedule. Used by the calendar picker to ring days as the user
 * scrolls. Same precedence as the chip/badge layer:
 * `'scheduled'` (overlap with existing shift) wins over `'unavailable'`.
 *
 * @param {{
 *   caregiverSchedule?: Array<{ type: string, date?: string, start_time?: string, end_time?: string }>,
 *   structure?: { from_time: string, to_time: string },
 *   date: (string | Date | import('moment').Moment),
 *   facilityTimezone?: string
 * }} args
 * @returns {('scheduled' | 'unavailable' | null)}
 */
export const getDateConflictForStructure = ({ caregiverSchedule = [], structure, date, facilityTimezone }) => {
	if (!facilityTimezone) {
		return null;
	}
	const isoDate = moment(date).format('YYYY-MM-DD');

	if (structure) {
		const proposed = computeProposedWindow(structure, date, facilityTimezone);
		const hasShiftOverlap = caregiverSchedule.some((entry) => entry.type === 'shift' && shiftsOverlap(entry, proposed));
		if (hasShiftOverlap) {
			return 'scheduled';
		}
	}

	const isUnavailable = caregiverSchedule.some((entry) => entry.type !== 'shift' && entry.date === isoDate);
	return isUnavailable ? 'unavailable' : null;
};

const computeBulkAssignOverlaps = ({ caregiverSchedule, shiftsGroups, shiftStructures, facilityTimezone }) => {
	if (!facilityTimezone) {
		return {};
	}

	const structuresById = new Map();
	(shiftStructures || []).forEach((structure) => structuresById.set(structure.id, structure));

	const entries = [];
	(shiftsGroups || []).forEach((group) => {
		const structure = structuresById.get(group.shiftStructureId);
		if (!structure || !group.dates || group.dates.length === 0) {
			return;
		}
		group.dates.forEach((date) => {
			entries.push({
				groupId: group.id,
				structure,
				isoDate: moment(date).format('YYYY-MM-DD'),
				...computeProposedWindow(structure, date, facilityTimezone)
			});
		});
	});

	const result = {};
	const addDateToField = (groupId, field, isoDate) => {
		if (!result[groupId]) {
			result[groupId] = {};
		}
		if (!result[groupId][field]) {
			result[groupId][field] = new Set();
		}
		result[groupId][field].add(isoDate);
	};

	for (let i = 0; i < entries.length; i += 1) {
		const entry = entries[i];
		const conflict = getDateConflictForStructure({
			caregiverSchedule,
			structure: entry.structure,
			date: entry.isoDate,
			facilityTimezone
		});
		if (conflict === 'scheduled') {
			addDateToField(entry.groupId, 'overlappingDates', entry.isoDate);
		} else if (conflict === 'unavailable') {
			addDateToField(entry.groupId, 'unavailableDates', entry.isoDate);
		}
	}

	for (let i = 0; i < entries.length; i += 1) {
		for (let j = i + 1; j < entries.length; j += 1) {
			const a = entries[i];
			const b = entries[j];
			if (a.start < b.end && b.start < a.end) {
				addDateToField(a.groupId, 'internalOverlapDates', a.isoDate);
				addDateToField(b.groupId, 'internalOverlapDates', b.isoDate);
			}
		}
	}

	return result;
};

export default computeBulkAssignOverlaps;
